import LazyLoader from 'Components/LazyLoader';
import { useEntityAnalytics } from 'Containers/EntityProfileV2/hooks/useEntityAnalytics';
import { useEntityDetails } from 'Containers/EntityProfileV2/hooks/useEntityDetailsLoader';
import { MobXProviderContext, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { ScrollArea } from '~/lib/ui/scroll-area';
import {
  SidebarContent,
  SidebarHeader,
  SidebarSeparator,
} from '~/lib/ui/sidebar';
import { cn } from '~/lib/utils';
import { EntityProfileActions } from '../EntityProfileActions';
import { EntityProfileContactDetails } from './components/EntityProfileContactDetails';
import { EntityProfileHeader } from './components/EntityProfileHeader';
import { EntityProfileSidebarProps } from './index.types';

export const EntityProfileSidebar = observer(
  ({ hidden, closePanel, openContactDetails }: EntityProfileSidebarProps) => {
    const {
      conversation: { CurrentConversation },
    } = useContext(MobXProviderContext) as RootStoreProps;

    const [isLoadingComplete, setIsLoadingComplete] = useState(false);

    const { getDetails } = useEntityDetails();

    // Get entity details reactively from MobX store
    const entityDetails = getDetails();

    // Handle analytics tracking
    useEntityAnalytics(entityDetails);

    useEffect(() => {
      setIsLoadingComplete(false);
      const timer = setTimeout(() => {
        setIsLoadingComplete(true);
      }, 500);

      return () => clearTimeout(timer);
    }, [
      entityDetails?.unsavedContact,
      entityDetails?.person,
      entityDetails?.contacts,
    ]);

    if (!isLoadingComplete || !entityDetails) {
      return (
        <div
          className={cn('flex justify-center items-center h-full', {
            hidden,
          })}
        >
          <LazyLoader delay={0} isLoading />
        </div>
      );
    }

    return (
      <ScrollArea className={cn('h-full pb-3.5', { hidden })}>
        <SidebarHeader>
          <EntityProfileHeader
            unsavedContact={entityDetails?.unsavedContact}
            contacts={entityDetails?.contacts}
            person={entityDetails?.person}
            closePanel={closePanel}
          />
        </SidebarHeader>

        <SidebarContent>
          <EntityProfileActions conversation={CurrentConversation} />

          <div className="px-3.5">
            <SidebarSeparator />
          </div>

          <EntityProfileContactDetails
            unsavedContact={entityDetails?.unsavedContact}
            contacts={entityDetails?.contacts}
            person={entityDetails?.person}
            openContactDetails={openContactDetails}
          />
        </SidebarContent>
      </ScrollArea>
    );
  }
);
